import {
  Award,
  CheckCircle2,
  CreditCard,
  Gem,
  Handshake,
  Heart,
  Music4,
  Shirt,
  TrendingUp,
  Utensils,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import logo from "../../assets/logo_no_background.png";
import { getWelcomeContent } from "../../services/welcome.service";

// Icon mapping
const iconMap: any = {
  Award,
  CheckCircle2,
  CreditCard,
  Gem,
  Handshake,
  Heart,
  Music4,
  Shirt,
  TrendingUp,
  Utensils,
  X,
};

// Default Fallback Data (used if API returns empty)
const defaultImages = [1, 2, 3, 5, 6, 7, 8].map(
  (i) => `https://praceria.com.br/wp-content/uploads/2025/07/${i}.jpeg`
);

const defaultBenefits = [
  {
    icon: "TrendingUp",
    title: "Visibilidade Real",
    description: "Sua marca vista por milhares de visitantes em cada edição",
  },
  {
    icon: "Handshake",
    title: "Networking Qualificado",
    description: "Conecte-se com outros empreendedores e potenciais parceiros",
  },
  {
    icon: "Award",
    title: "Crescimento Sustentável",
    description: "Oportunidades reais de negócio e expansão da sua marca",
  },
  {
    icon: "Heart",
    title: "Impacto Social",
    description: "Participe de um movimento que gera transformação social",
  },
  {
    icon: "CreditCard",
    title: "Geração de Receita",
    description:
      "Venda diretamente para um público qualificado e aumente seu faturamento",
  },
  {
    icon: "CreditCard",
    title: "Curadoria de Marcas",
    description: "Mix estratégico para maximizar seus resultados",
  },
];

const defaultStats = [
  { description: "70+", title: "Marcas Autorais" },
  { description: "2000+", title: "Visitantes por Edição" },
  { description: "9+", title: "Edições Realizadas" },
  { description: "100%", title: "Foco no Seu Sucesso" },
];

const defaultCategories = [
  { icon: "Shirt", title: "Moda" },
  { icon: "Utensils", title: "Gastronomia" },
  { icon: "Gem", title: "Acessórios" },
  { icon: "Music4", title: "Música ao vivo" },
];

export default function Welcome() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [heroData, setHeroData] = useState<any>(null);
  const [images, setImages] = useState<string[]>([]);
  const [benefits, setBenefits] = useState<any[]>([]);
  const [stats, setStats] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [aboutData, setAboutData] = useState<any[]>([]);
  const [organizationData, setOrganizationData] = useState<any[]>([]);
  const [impactData, setImpactData] = useState<any[]>([]);
  const [footerData, setFooterData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          heroRes,
          galleryRes,
          benefitsRes,
          statsRes,
          categoriesRes,
          aboutRes,
          organizationRes,
          impactRes,
          footerRes,
        ] = await Promise.all([
          getWelcomeContent("hero"),
          getWelcomeContent("gallery"),
          getWelcomeContent("benefits"),
          getWelcomeContent("stats"),
          getWelcomeContent("categories"),
          getWelcomeContent("about"),
          getWelcomeContent("organization"),
          getWelcomeContent("impact"),
          getWelcomeContent("footer"),
        ]);

        if (heroRes.length > 0) setHeroData(heroRes[0]);

        if (galleryRes.length > 0) {
          setImages(galleryRes.map((item: any) => item.image_url));
        } else {
          setImages(defaultImages);
        }

        if (benefitsRes.length > 0) {
          setBenefits(benefitsRes);
        } else {
          setBenefits(defaultBenefits);
        }

        if (statsRes.length > 0) {
          setStats(statsRes);
        } else {
          setStats(defaultStats);
        }

        if (categoriesRes.length > 0) {
          setCategories(categoriesRes);
        } else {
          setCategories(defaultCategories);
        }

        if (aboutRes.length > 0) setAboutData(aboutRes);
        if (organizationRes.length > 0) setOrganizationData(organizationRes);
        if (impactRes.length > 0) setImpactData(impactRes);
      } catch (error) {
        console.error("Error fetching welcome content:", error);
        // Fallback to defaults on error
        setImages(defaultImages);
        setBenefits(defaultBenefits);
        setStats(defaultStats);
        setCategories(defaultCategories);
      }
    };

    fetchData();
  }, []);

  const renderIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName] || CheckCircle2;
    return <IconComponent className="w-5 h-5" />;
  };

  const renderCategoryIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName] || CheckCircle2;
    return <IconComponent className="w-6 h-6" />;
  };

  return (
    <div className="max-w-[1200px] mx-auto">
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative w-full max-w-5xl h-full flex items-center justify-center">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors bg-black/50 p-2 rounded-full"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={selectedImage}
              alt="Full view"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      <section className="relative overflow-hidden rounded-2xl min-h-[500px] md:min-h-[460px] flex items-center justify-center">
        <img
          src={
            heroData?.image_url ||
            "https://praceria.com.br/wp-content/uploads/2025/07/1.jpeg"
          }
          alt="Praceria"
          className="absolute inset-0 w-full h-full object-cover filter grayscale-[20%]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#24324D]/40 to-[#151c2c]/80" />
        <div className="relative z-10 text-center text-white px-6 flex flex-col items-center py-12">
          <div className="flex flex-col items-center">
            <img src={logo} alt="Logo" className="h-28 w-auto mb-4" />
            <h1 className="text-3xl md:text-4xl font-bold tracking-wide">
              {heroData?.title || "Conheça a Praceria"}
            </h1>
            <p className="mt-2 text-white/80 max-w-xl text-xl mx-auto leading-relaxed">
              {heroData?.description || (
                <>
                  Conectamos expositores autorais<br></br>para criar eventos
                  únicos e memoráveis.
                </>
              )}
            </p>
            <div className="mt-4 inline-flex items-center text-lg gap-2 bg-white/10 text-white px-4 py-2 rounded-full">
              <CheckCircle2 className="w-5 h-5" />
              <span>Ecossistema de marcas autorais e experiências reais</span>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x mt-8 border rounded-lg">
        {stats.map((stat, i) => (
          <div key={i} className="text-center p-6">
            <div className="text-[#24324D] text-3xl font-light">
              {stat.description}
            </div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mt-1">
              {stat.title}
            </div>
          </div>
        ))}
      </section>

      <section className="mt-10">
        <h2 className="text-2xl font-light text-center mb-6 text-[#24324D]">
          Por que fazer parte?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {benefits.map((benefit, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#24324D] text-white flex items-center justify-center rounded-md">
                {renderIcon(benefit.icon)}
              </div>
              <div>
                <div className="text-[#24324D] font-medium mb-1">
                  {benefit.title}
                </div>
                <div className="text-slate-600 text-sm leading-relaxed">
                  {benefit.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
        {categories.map((category, i) => (
          <div
            key={i}
            className="flex items-center gap-3 p-4 bg-teal-600 text-white rounded-md"
          >
            {renderCategoryIcon(category.icon)}
            <span className="font-medium">{category.title}</span>
          </div>
        ))}
      </section>

      <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
        <div className="md:col-span-2">
          <h3 className="text-xl font-light mb-4 text-[#24324D]">
            {aboutData.length > 0
              ? aboutData[0].title
              : "Você em boa companhia"}
          </h3>
          {aboutData.length > 0 ? (
            aboutData.map((item, i) => (
              <p key={i} className="text-[#374151] leading-8 mb-3">
                {item.description}
              </p>
            ))
          ) : (
            <>
              <p className="text-[#374151] leading-8 mb-3">
                É com alegria que te recebemos na plataforma de expositores da
                Praceria, um espaço pensado pra quem cria com propósito e
                acredita no poder das conexões.
              </p>
              <p className="text-[#374151] leading-8 mb-3">
                Aqui, a gente abre portas para oportunidades reais de trabalho e
                visibilidade, fortalecendo quem está por trás das marcas
                autorais que inspiram e transformam.
              </p>
              <p className="text-[#374151] leading-8">
                A Praceria é mais que um evento, é um movimento: cheio de
                encontros e negócios. A cada edição, buscamos dar um passo
                adiante, lado a lado com você, para encantar ainda mais quem nos
                visita.
              </p>
            </>
          )}
        </div>
        <div>
          <h4 className="text-lg font-light mb-4 text-[#24324D]">
            {organizationData.length > 0
              ? organizationData[0].title
              : "Como tudo é organizado?"}
          </h4>
          <ul className="space-y-3">
            {organizationData.length > 0 ? (
              organizationData.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-[#24324D]" />
                  <span className="text-[#374151] text-sm">
                    {item.description}
                  </span>
                </li>
              ))
            ) : (
              <>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-[#24324D]" />
                  <span className="text-[#374151] text-sm">
                    ~70 marcas autorais
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-[#24324D]" />
                  <span className="text-[#374151] text-sm">
                    Festival gastronômico
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-[#24324D]" />
                  <span className="text-[#374151] text-sm">Música ao vivo</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-[#24324D]" />
                  <span className="text-[#374151] text-sm">
                    Oficinas lúdicas
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-[#24324D]" />
                  <span className="text-[#374151] text-sm">
                    Atrações a cada edição
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-[#24324D]" />
                  <span className="text-[#374151] text-sm">
                    Ambiente familiar e seguro
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 bg-[#24324D]" />
                  <span className="text-[#374151] text-sm">
                    Presença de projetos sociais
                  </span>
                </li>
              </>
            )}
          </ul>
        </div>
      </section>

      <section className="mt-12 text-center border rounded-lg p-8 bg-slate-50">
        <h3 className="text-xl font-light text-[#24324D] mb-3">
          {impactData.length > 0 ? impactData[0].title : "Impacto Social Real"}
        </h3>
        {impactData.length > 0 ? (
          impactData.map((item, i) => (
            <p
              key={i}
              className="text-[#374151] leading-7 max-w-xl mx-auto mb-2"
            >
              {item.description}
            </p>
          ))
        ) : (
          <>
            <p className="text-[#374151] leading-7 max-w-xl mx-auto mb-2">
              A cada edição, recebemos ONGs e projetos sociais, totalmente
              subsidiados pelo evento. Isso significa que, ao participar, você
              também fortalece iniciativas que geram impacto social positivo.
            </p>
            <p className="font-medium text-[#24324D]">
              Não com doações, mas com trabalho, visibilidade e autonomia.
            </p>
          </>
        )}
      </section>

      <section className="mt-12">
        <h3 className="text-xl font-light text-center text-[#24324D] mb-2">
          Galeria de Fotos
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((image, i) => (
            <img
              key={i}
              src={image}
              alt="Praceria Event"
              className="w-full h-40 md:h-56 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setSelectedImage(image)}
            />
          ))}
        </div>
      </section>

      <section className="text-center py-10">
        <h3 className="text-xl font-light text-[#24324D] mb-2">
          Esse é só o começo
        </h3>
        <p className="text-[#374151] leading-7 max-w-md mx-auto">
          Quando sua marca for aprovada, daremos início a uma jornada de
          crescimento juntos!
        </p>
      </section>
    </div>
  );
}
